import { useEffect, useState, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, ArrowLeft, Upload, Loader2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import api from '../lib/api';
import { money } from '../lib/money';
import { Button, Card, EmptyState, PageHeader, Tag } from '../components/ui';

type ApiErr = { response?: { status?: number; data?: { detail?: string } }; message?: string };

interface ExtractorResult {
  extractor: string;
  mode?: string;
  parser?: string;
  one_step?: boolean;
  extraction_mode?: string;
  ok: boolean;
  error: string | null;
  text_chars?: number;
  markdown_chars?: number;
  sample_text?: string;
  sample_markdown?: string;
  num_pages: number;
  extract_seconds: number;
  extract_cost_usd: number;
  text_quality_score?: number;
  transactions?: Array<{ date?: string; description?: string; amount?: number }>;
  transaction_count: number;
  sum_of_amounts: number;
  beginning_balance_extracted?: number | null;
  ending_balance_extracted: number | null;
  reconciliation_delta: number | null;
  printed_summary?: {
    previous_balance: number | null;
    payments_credits_total: number | null;
    purchases_total: number | null;
    fees_total: number | null;
    interest_total: number | null;
    new_balance: number | null;
  } | null;
  summary_reconciliation?: {
    extracted_credits_total: number;
    extracted_charges_total: number;
    printed_credits_total: number | null;
    printed_charges_total: number | null;
    credits_diff: number | null;
    charges_diff: number | null;
    printed_internal_delta: number | null;
    pinpoint: string | null;
  } | null;
  ai_parse_seconds: number;
  ai_parse_cost_usd?: number;
  pipeline_path?: string;
  detection_info?: string | null;
  warnings: string[];
}

interface BenchmarkResponse {
  filename: string;
  file_size_bytes: number;
  page_count: number;
  pdfplumber: ExtractorResult;
  llamaparse: ExtractorResult;
  llamaparse_agentic?: ExtractorResult;
  llamaparse_agentic_gpt5?: ExtractorResult;
  llamaparse_agentic_gpt5_mini?: ExtractorResult;
  llamaparse_agentic_claude?: ExtractorResult;
  llamaparse_agentic_claude_sonnet?: ExtractorResult;
  claude_vision?: ExtractorResult;
  llamaextract?: ExtractorResult;
  production?: ExtractorResult;
  diff: {
    only_in_pdfplumber: Array<{ date?: string; description?: string; amount?: number }>;
    only_in_llamaparse: Array<{ date?: string; description?: string; amount?: number }>;
    common_count: number;
  };
  benchmark_id?: number;
}

interface HistoryItem {
  id: number;
  filename: string | null;
  file_size_bytes: number | null;
  page_count: number | null;
  results: Record<string, Partial<ExtractorResult>> | null;
  created_at: string | null;
}

interface InventoryRosterRow {
  constant: string;
  model: string;
  role: string;
}

interface InventoryTask {
  task: string;
  description: string;
  mechanics: string;
  models: string[];
  files: string[];
}

interface AiInventory {
  roster: InventoryRosterRow[];
  tasks: InventoryTask[];
  agentic_note: string;
}

const fmtMoney = (v: number | null | undefined) => money(v);

const DATETIME = new Intl.DateTimeFormat('en-US', { dateStyle: 'short', timeStyle: 'medium' });
const COUNT = new Intl.NumberFormat('en-US');

const fmtBytes = (b: number) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function ExtractorBenchmarkPage() {
  const [forbidden, setForbidden] = useState(false);
  const [llamaAvailable, setLlamaAvailable] = useState<boolean | null>(null);
  const [llamaReason, setLlamaReason] = useState<string | null>(null);
  const [skipAiParse, setSkipAiParse] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BenchmarkResponse | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loadingDetailId, setLoadingDetailId] = useState<number | null>(null);
  const [showPdfplumberText, setShowPdfplumberText] = useState(false);
  const [showLlamaMarkdown, setShowLlamaMarkdown] = useState(false);
  const [showDiff, setShowDiff] = useState(true);
  const [showInventory, setShowInventory] = useState(false);
  const [inventory, setInventory] = useState<AiInventory | null>(null);
  const [inventoryError, setInventoryError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Lazy-load the AI inventory on first expand — it re-scans server source, so
  // there's no point paying for it unless the panel is actually opened.
  const toggleInventory = async () => {
    const next = !showInventory;
    setShowInventory(next);
    if (next && !inventory) {
      try {
        const r = await api.get('/extractor-benchmark/ai-inventory');
        setInventory(r.data);
      } catch (raw) {
        const e = raw as ApiErr;
        setInventoryError(e?.response?.data?.detail || e?.message || 'Could not load AI inventory');
      }
    }
  };

  const loadStatus = async () => {
    try {
      const r = await api.get('/extractor-benchmark/whoami');
      if (!r.data.is_admin) {
        setForbidden(true);
        return;
      }
      setLlamaAvailable(r.data.llamaparse_available);
      setLlamaReason(r.data.llamaparse_unavailable_reason);
      const h = await api.get('/extractor-benchmark/history', { params: { limit: 50 } });
      setHistory(h.data.items || []);
    } catch (raw) {
      const e = raw as ApiErr;
      if (e?.response?.status === 403) setForbidden(true);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  // Shape the raw API payload (from GET /extractor-benchmark/{id}, used both by
  // the live poll and by re-opening a past run) into the BenchmarkResponse the
  // result cards render from.
  const mapResults = (data: {
    id?: number;
    filename: string;
    file_size_bytes: number;
    page_count: number;
    results?: Partial<BenchmarkResponse> | null;
  }): BenchmarkResponse => {
    const res: Partial<BenchmarkResponse> = data.results || {};
    return {
      filename: data.filename,
      file_size_bytes: data.file_size_bytes,
      page_count: data.page_count,
      pdfplumber: res.pdfplumber!,
      llamaparse: res.llamaparse!,
      llamaparse_agentic: res.llamaparse_agentic,
      llamaparse_agentic_gpt5: res.llamaparse_agentic_gpt5,
      llamaparse_agentic_gpt5_mini: res.llamaparse_agentic_gpt5_mini,
      llamaparse_agentic_claude: res.llamaparse_agentic_claude,
      llamaparse_agentic_claude_sonnet: res.llamaparse_agentic_claude_sonnet,
      claude_vision: res.claude_vision,
      llamaextract: res.llamaextract,
      production: res.production,
      diff: {
        only_in_pdfplumber: res.diff?.only_in_pdfplumber || [],
        only_in_llamaparse: res.diff?.only_in_llamaparse || [],
        common_count: res.diff?.common_count || 0,
      },
      benchmark_id: data.id,
    };
  };

  // Re-open a past benchmark's full detail (cards + diff) by fetching its saved
  // results — nothing is lost when you leave the page, it all lives server-side.
  const viewBenchmark = async (id: number) => {
    setLoadingDetailId(id);
    setError(null);
    try {
      const r = await api.get(`/extractor-benchmark/${id}`, { timeout: 30_000 });
      if (r.data?.status === 'running') {
        setSelectedId(id);
        setError('That benchmark is still running — open it again once it finishes.');
        return;
      }
      if (r.data?.status === 'error') {
        setError(r.data?.results?.error || 'That benchmark failed during processing.');
        setResult(null);
        setSelectedId(id);
        return;
      }
      setResult(mapResults(r.data));
      setSelectedId(id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (raw) {
      const e = raw as ApiErr;
      setError(e?.response?.data?.detail || e?.message || 'Could not load that benchmark');
    } finally {
      setLoadingDetailId(null);
    }
  };

  const runBenchmark = async () => {
    if (!file) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('skip_ai_parse', skipAiParse ? 'true' : 'false');
      // The run kicks off in the background and returns an id immediately —
      // we then poll until it's done (the four extractors can take minutes,
      // which is too long for a single synchronous request).
      const start = await api.post('/extractor-benchmark', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000,
      });
      const id = start.data?.benchmark_id;
      if (!id) throw new Error('No benchmark id returned');

      const deadline = Date.now() + 15 * 60 * 1000; // 15 min safety cap
      // poll loop
      while (true) {
        if (Date.now() > deadline) {
          throw new Error('Benchmark timed out after 15 minutes');
        }
        await new Promise((res) => setTimeout(res, 3000));
        let poll;
        try {
          poll = await api.get(`/extractor-benchmark/${id}`, { timeout: 30_000 });
        } catch {
          continue; // transient network hiccup — keep polling
        }
        const status = poll.data?.status;
        if (status === 'done') {
          setResult(mapResults(poll.data));
          setSelectedId(id);
          loadStatus();
          break;
        }
        if (status === 'error') {
          throw new Error(poll.data?.results?.error || 'Benchmark failed during processing');
        }
        // status === 'running' → keep polling
      }
    } catch (raw) {
      const e = raw as ApiErr;
      setError(e?.response?.data?.detail || e?.message || 'Benchmark failed');
    } finally {
      setRunning(false);
    }
  };

  if (forbidden) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card padding="none">
          <EmptyState
            icon={FlaskConical}
            title="Admin access required"
            action={
              <Link to="/" className="text-green-700 hover:underline inline-flex items-center gap-1">
                <ArrowLeft size={16} /> Back to cases
              </Link>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        size="title"
        title={
          <span className="flex items-center gap-2">
            <FlaskConical size={24} />
            Extractor Benchmark
          </span>
        }
        description="Side-by-side bake-off on a single PDF: pdfplumber (free baseline), LlamaParse Premium, LlamaParse Agentic Plus structured by GPT-4.1, GPT-5, GPT-5 mini, Claude Opus and Claude Sonnet (same markdown, different model), standalone Claude Opus vision (reads the raw PDF directly), and one-step LlamaExtract. Nothing here touches your real cases."
        actions={
          <Link to="/" className="text-sm text-ink-soft hover:text-green-700 inline-flex items-center gap-1">
            <ArrowLeft size={16} /> Back
          </Link>
        }
      />

      {llamaAvailable === false && (
        <div className="mb-4 p-4 bg-warn-soft border border-warn-accent rounded-control flex items-start gap-2">
          <AlertTriangle size={16} className="text-warn mt-(--space-hair)" />
          <div className="text-sm text-warn">
            <div className="font-medium">LlamaParse unavailable</div>
            <div>{llamaReason}</div>
          </div>
        </div>
      )}

      <Card padding="spacious" className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          {/* eslint-disable-next-line no-restricted-syntax -- hidden file input; no ui primitive exists for file pickers */}
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <Button
            variant="secondary"
            size="lg"
            icon={Upload}
            onClick={() => inputRef.current?.click()}
          >
            {file ? file.name : 'Choose PDF'}
          </Button>
          {file && (
            <span className="text-xs text-ink-faint">{fmtBytes(file.size)}</span>
          )}

          <div className="flex items-center gap-4 ml-auto">
            <label className="text-sm text-ink-soft inline-flex items-center gap-2 cursor-pointer">
              {/* eslint-disable-next-line no-restricted-syntax -- no Checkbox primitive in components/ui yet */}
              <input
                type="checkbox"
                checked={skipAiParse}
                onChange={(e) => setSkipAiParse(e.target.checked)}
                disabled={running}
                className="rounded"
              />
              Skip AI parse (raw text only)
            </label>
          </div>

          <Button
            size="lg"
            icon={FlaskConical}
            loading={running}
            onClick={runBenchmark}
            disabled={!file || llamaAvailable === false}
            title="Runs all extractors side-by-side"
          >
            {running ? 'Running…' : 'Run Benchmark'}
          </Button>
        </div>

        {running && (
          <div className="text-sm text-green-900 bg-cream border border-border rounded-control p-3 inline-flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            Running all extractors in the background — this can take a few minutes for multi-page statements. You can leave this page open.
          </div>
        )}

        {error && (
          <div className="text-sm text-danger bg-danger-soft border border-danger rounded-control p-3">
            {error}
          </div>
        )}
      </Card>

      <Card padding="default" className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleInventory}
          className="px-0 text-sm hover:bg-transparent hover:underline"
        >
          {showInventory ? '▾' : '▸'} AI inventory — which model runs which task
        </Button>
        {showInventory && (
          <div className="mt-3">
            {inventoryError && (
              <div className="text-sm text-danger bg-danger-soft border border-danger rounded-control p-3">{inventoryError}</div>
            )}
            {!inventory && !inventoryError && (
              <div className="text-sm text-ink-faint inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Scanning source…
              </div>
            )}
            {inventory && (
              <>
                <p className="text-xs text-ink-faint mb-3">
                  Live-scanned from the server source (same code that generates docs/ai-inventory.md) — this can't go stale.
                </p>
                <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6 items-start">
                  <div className="overflow-x-auto">
                    <div className="t-eyebrow mb-2">Model roster (server/model_config.py)</div>
                    {/* eslint-disable-next-line no-restricted-syntax -- restyled in place; wrapping multi-line cells don't fit DataTable's fixed row heights */}
                    <table className="w-full text-sm">
                      <thead className="t-eyebrow text-left border-b border-border">
                        <tr>
                          <th className="py-2 pr-3 font-semibold">Constant</th>
                          <th className="pr-3 font-semibold">Model</th>
                          <th className="font-semibold">Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventory.roster.map((r) => (
                          <tr key={r.constant} className="border-b border-border-subtle align-top">
                            <td className="py-2 pr-3 t-ident text-green-900 whitespace-nowrap">{r.constant}</td>
                            <td className="pr-3 t-ident text-ink whitespace-nowrap">{r.model}</td>
                            <td className="text-ink-soft">{r.role}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-ink-soft mt-3 leading-relaxed">{inventory.agentic_note}</p>
                  </div>
                  <div className="overflow-x-auto">
                    <div className="t-eyebrow mb-2">AI tasks ({inventory.tasks.length})</div>
                    {/* eslint-disable-next-line no-restricted-syntax -- restyled in place; wrapping multi-line cells don't fit DataTable's fixed row heights */}
                    <table className="w-full text-sm">
                      <thead className="t-eyebrow text-left border-b border-border">
                        <tr>
                          <th className="py-2 pr-3 font-semibold">Task</th>
                          <th className="pr-3 font-semibold">What it does</th>
                          <th className="pr-3 font-semibold">Model(s)</th>
                          <th className="font-semibold">Where</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventory.tasks.map((t) => (
                          <tr key={t.task} className="border-b border-border-subtle align-top">
                            <td className="py-2 pr-3 t-ident text-green-900 whitespace-nowrap">
                              {t.task}
                              {t.mechanics.includes('tool output') && (
                                <Tag size="sm" tone="warn" title={t.mechanics} className="ml-2">tool-forced</Tag>
                              )}
                            </td>
                            <td className="pr-3 text-ink-soft">{t.description}</td>
                            <td className="pr-3 t-ident text-ink whitespace-nowrap">{t.models.map((m) => <div key={m}>{m}</div>)}</td>
                            <td className="t-ident text-ink-faint whitespace-nowrap">{t.files.map((f) => <div key={f}>{f}</div>)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Card>

      {result && (
        <>
          {!running && selectedId !== null && (
            <div className="mb-4 px-4 py-2 bg-cream border border-border rounded-control text-sm text-green-900 flex items-center gap-2">
              <FlaskConical size={16} />
              <span>
                Viewing saved results for <span className="font-medium">{result.filename || 'this run'}</span>
                {result.page_count ? ` (${result.page_count} pages)` : ''}. Run a new PDF above anytime — past runs stay here.
              </span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {result.production && (
              <ResultCard label="Current production pipeline" sublabel="The REAL end-to-end app flow today: pdfplumber → quality gate → bank ID → page-by-page parse → validation → Claude vision fallback" r={result.production} />
            )}
            <ResultCard label="pdfplumber → GPT-4.1" sublabel="Stripped baseline: whole-doc single shot, generic prompt, no vision fallback" r={result.pdfplumber} />
            <ResultCard label="LlamaParse Premium → GPT-4.1" sublabel="Two-step: parse to markdown, then AI extract" r={result.llamaparse} />
            {result.llamaparse_agentic && (
              <ResultCard label="LlamaParse Agentic Plus → GPT-4.1" sublabel="Highest LlamaParse tier (agentic vision)" r={result.llamaparse_agentic} />
            )}
            {result.llamaparse_agentic_gpt5 && (
              <ResultCard label="LlamaParse Agentic Plus → GPT-5" sublabel="Same Agentic Plus markdown, structured by GPT-5 (reasoning) instead of GPT-4.1" r={result.llamaparse_agentic_gpt5} />
            )}
            {result.llamaparse_agentic_gpt5_mini && (
              <ResultCard label="LlamaParse Agentic Plus → GPT-5 mini" sublabel="Same markdown — GPT-5 mini is the cheapest reasoning option; compare accuracy per dollar" r={result.llamaparse_agentic_gpt5_mini} />
            )}
            {result.llamaparse_agentic_claude && (
              <ResultCard label="LlamaParse Agentic Plus → Claude Opus" sublabel="Same Agentic Plus markdown, structured by Claude Opus instead of GPT-4.1" r={result.llamaparse_agentic_claude} />
            )}
            {result.llamaparse_agentic_claude_sonnet && (
              <ResultCard label="LlamaParse Agentic Plus → Claude Sonnet" sublabel="Same markdown as the Opus lane — Sonnet is ~5x cheaper, so compare accuracy head-to-head" r={result.llamaparse_agentic_claude_sonnet} />
            )}
            {result.claude_vision && (
              <ResultCard label="Claude Opus (vision, standalone)" sublabel="Opus reads the raw PDF pages directly — no LlamaParse OCR in front of it. One-model end-to-end extraction" r={result.claude_vision} />
            )}
            {result.llamaextract && (
              <ResultCard label="LlamaExtract (one-step)" sublabel="Schema-driven structured output, no separate AI parse" r={result.llamaextract} />
            )}
          </div>

          <Card padding="default" className="mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDiff((v) => !v)}
              className="px-0 text-sm hover:bg-transparent hover:underline mb-2"
            >
              {showDiff ? '▾' : '▸'} Transaction diff ({result.diff.common_count} common, {result.diff.only_in_pdfplumber.length} pdfplumber-only, {result.diff.only_in_llamaparse.length} llamaparse-only)
            </Button>
            {showDiff && (
              <div className="grid grid-cols-2 gap-4 mt-3">
                <DiffList title="Only in pdfplumber" items={result.diff.only_in_pdfplumber} />
                <DiffList title="Only in LlamaParse" items={result.diff.only_in_llamaparse} />
              </div>
            )}
          </Card>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <RawTextPanel
              label="pdfplumber raw text"
              visible={showPdfplumberText}
              onToggle={() => setShowPdfplumberText((v) => !v)}
              text={result.pdfplumber.sample_text || ''}
              chars={result.pdfplumber.text_chars || 0}
            />
            <RawTextPanel
              label="LlamaParse markdown"
              visible={showLlamaMarkdown}
              onToggle={() => setShowLlamaMarkdown((v) => !v)}
              text={result.llamaparse.sample_markdown || ''}
              chars={result.llamaparse.markdown_chars || 0}
            />
          </div>
        </>
      )}

      <Card padding="default">
        <h2 className="t-card text-green-900 mb-1">Past benchmarks</h2>
        {history.length === 0 ? (
          <p className="text-sm text-ink-faint">No benchmarks run yet.</p>
        ) : (
          <>
          <p className="text-xs text-ink-faint mb-3">Click any row to reopen its full results, cards, and transaction diff above.</p>
          <div className="overflow-x-auto">
          {/* eslint-disable-next-line no-restricted-syntax -- restyled in place; 23-column history matrix is beyond a cheap DataTable conversion */}
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="t-eyebrow text-left border-b border-border">
              <tr>
                <th className="py-2 font-semibold">When</th>
                <th className="font-semibold">File</th>
                <th className="font-semibold">Pages</th>
                <th className="font-semibold">Production txns / Δ</th>
                <th className="font-semibold">pdfplumber txns / Δ</th>
                <th className="font-semibold">Premium txns / Δ</th>
                <th className="font-semibold">Agentic·GPT txns / Δ</th>
                <th className="font-semibold">Agentic·GPT5 txns / Δ</th>
                <th className="font-semibold">Agentic·GPT5mini txns / Δ</th>
                <th className="font-semibold">Agentic·Opus txns / Δ</th>
                <th className="font-semibold">Agentic·Sonnet txns / Δ</th>
                <th className="font-semibold">Opus·vision txns / Δ</th>
                <th className="font-semibold">LlamaExtract txns / Δ</th>
                <th className="font-semibold">Production path</th>
                <th className="font-semibold">Production $</th>
                <th className="font-semibold">Premium $</th>
                <th className="font-semibold">Agentic $</th>
                <th className="font-semibold">GPT5 parse $</th>
                <th className="font-semibold">GPT5mini parse $</th>
                <th className="font-semibold">Extract $</th>
                <th className="font-semibold">Opus parse $</th>
                <th className="font-semibold">Sonnet parse $</th>
                <th className="font-semibold">Opus·vision $</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => {
                const pp = h.results?.pdfplumber || {};
                const lp = h.results?.llamaparse || {};
                const ag = h.results?.llamaparse_agentic || {};
                const ag5 = h.results?.llamaparse_agentic_gpt5 || {};
                const ag5m = h.results?.llamaparse_agentic_gpt5_mini || {};
                const agc = h.results?.llamaparse_agentic_claude || {};
                const ags = h.results?.llamaparse_agentic_claude_sonnet || {};
                const cv = h.results?.claude_vision || {};
                const le = h.results?.llamaextract || {};
                const prod = h.results?.production || {};
                const isSelected = selectedId === h.id;
                const isLoading = loadingDetailId === h.id;
                return (
                  <tr
                    key={h.id}
                    onClick={() => viewBenchmark(h.id)}
                    title="Click to reopen this benchmark's full results"
                    className={`border-b border-border-subtle cursor-pointer transition-colors ${
                      isSelected ? 'bg-cream' : 'hover:bg-cream-50'
                    }`}
                  >
                    <td className="py-2 text-ink-soft">
                      <span className="inline-flex items-center gap-2">
                        {isLoading && <Loader2 size={12} className="animate-spin text-green-700" />}
                        {h.created_at ? DATETIME.format(new Date(h.created_at)) : '—'}
                      </span>
                    </td>
                    <td className="text-ink">{h.filename || '—'}</td>
                    <td>{h.page_count ?? '—'}</td>
                    <HistoryTxnDeltaCell r={prod} />
                    <HistoryTxnDeltaCell r={pp} />
                    <HistoryTxnDeltaCell r={lp} />
                    <HistoryTxnDeltaCell r={ag} />
                    <HistoryTxnDeltaCell r={ag5} />
                    <HistoryTxnDeltaCell r={ag5m} />
                    <HistoryTxnDeltaCell r={agc} />
                    <HistoryTxnDeltaCell r={ags} />
                    <HistoryTxnDeltaCell r={cv} />
                    <HistoryTxnDeltaCell r={le} />
                    <td className={prod.pipeline_path && String(prod.pipeline_path).includes('vision') ? 'text-warn' : 'text-ink'}>{prod.pipeline_path || '—'}</td>
                    <td>{prod.ai_parse_cost_usd !== undefined ? fmtMoney(prod.ai_parse_cost_usd) : '—'}</td>
                    <td>{lp.extract_cost_usd !== undefined ? fmtMoney(lp.extract_cost_usd) : '—'}</td>
                    <td>{ag.extract_cost_usd !== undefined ? fmtMoney(ag.extract_cost_usd) : '—'}</td>
                    <td>{ag5.ai_parse_cost_usd !== undefined ? fmtMoney(ag5.ai_parse_cost_usd) : '—'}</td>
                    <td>{ag5m.ai_parse_cost_usd !== undefined ? fmtMoney(ag5m.ai_parse_cost_usd) : '—'}</td>
                    <td>{le.extract_cost_usd !== undefined ? fmtMoney(le.extract_cost_usd) : '—'}</td>
                    <td>{agc.ai_parse_cost_usd !== undefined ? fmtMoney(agc.ai_parse_cost_usd) : '—'}</td>
                    <td>{ags.ai_parse_cost_usd !== undefined ? fmtMoney(ags.ai_parse_cost_usd) : '—'}</td>
                    <td>{cv.ai_parse_cost_usd !== undefined ? fmtMoney(cv.ai_parse_cost_usd) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          </>
        )}
      </Card>
    </div>
  );
}

function ResultCard({ label, sublabel, r }: { label: string; sublabel?: string; r: ExtractorResult }) {
  const isOneStep = r.one_step === true || r.extractor === 'llamaextract';
  return (
    <Card padding="default" className={r.ok ? undefined : 'border-danger'}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="t-card text-green-900">{label}</h3>
          {sublabel && <p className="text-xs text-ink-faint mt-1">{sublabel}</p>}
        </div>
        {r.ok ? (
          <CheckCircle size={16} className="text-green-700 shrink-0" />
        ) : (
          <XCircle size={16} className="text-danger shrink-0" />
        )}
      </div>

      {!r.ok && r.error && (
        <div className="text-sm text-danger bg-danger-soft border border-danger rounded-control p-2 mb-3">
          {r.error}
        </div>
      )}

      <div className="space-y-2 text-sm">
        {r.pipeline_path && (
          <Row
            label="Pipeline path"
            value={r.pipeline_path}
            color={r.pipeline_path.includes('vision') ? 'text-warn' : 'text-green-900'}
            bold
          />
        )}
        {r.detection_info && <Row label="Bank detected" value={r.detection_info} />}
        <Row label="Pages" value={r.num_pages} />
        {!isOneStep && (
          <Row label={r.extractor === 'pdfplumber' ? 'Text chars' : 'Markdown chars'}
               value={COUNT.format(r.text_chars ?? r.markdown_chars ?? 0)} />
        )}
        {r.text_quality_score !== undefined && (
          <Row label="Text quality score" value={r.text_quality_score} />
        )}
        {isOneStep && r.extraction_mode && (
          <Row label="Extraction mode" value={r.extraction_mode} />
        )}
        <Row label="Transactions extracted" value={r.transaction_count} bold />
        <Row label="Sum of amounts" value={fmtMoney(r.sum_of_amounts)} />
        {r.beginning_balance_extracted !== undefined && (
          <Row label="Stated beginning balance" value={fmtMoney(r.beginning_balance_extracted)} />
        )}
        <Row label="Stated ending balance" value={fmtMoney(r.ending_balance_extracted)} />
        <Row
          label="Reconciliation delta"
          value={r.reconciliation_delta === null ? '—' : fmtMoney(r.reconciliation_delta)}
          color={r.reconciliation_delta === 0 ? 'text-green-700' : r.reconciliation_delta !== null ? 'text-warn' : ''}
          bold
        />
        {r.printed_summary && (
          <PrintedSummaryBlock summary={r.printed_summary} recon={r.summary_reconciliation} />
        )}
        <div className="border-t border-border-subtle pt-2 mt-2"></div>
        <Row label="Extract time" value={`${r.extract_seconds}s`} />
        {!isOneStep && <Row label="AI parse time" value={`${r.ai_parse_seconds}s`} />}
        <Row label="Extract cost" value={fmtMoney(r.extract_cost_usd)} />
        {!isOneStep && r.ai_parse_cost_usd !== undefined && (
          <Row label="AI parse cost" value={fmtMoney(r.ai_parse_cost_usd)} />
        )}
        {!isOneStep && r.ai_parse_cost_usd !== undefined && (
          <Row
            label="Total cost"
            value={fmtMoney((r.extract_cost_usd || 0) + (r.ai_parse_cost_usd || 0))}
            bold
          />
        )}
      </div>

      {r.warnings && r.warnings.length > 0 && (
        <div className="mt-3 text-xs text-warn">
          <div className="font-medium">Warnings:</div>
          <ul className="list-disc list-inside">
            {r.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </Card>
  );
}

function HistoryTxnDeltaCell({ r }: { r: Partial<ExtractorResult> }) {
  const count = r?.transaction_count;
  const delta = r?.reconciliation_delta;
  if (count === undefined || count === null) {
    return <td className="text-ink-faint">—</td>;
  }
  const deltaTxt =
    delta === null || delta === undefined ? '—' : fmtMoney(delta);
  return (
    <td>
      <span className="text-ink">{count}</span>
      <span className="text-ink-faint"> / </span>
      <span className={delta === 0 ? 'text-green-700' : delta !== null && delta !== undefined ? 'text-warn' : 'text-ink-faint'}>
        {deltaTxt}
      </span>
    </td>
  );
}

function Row({ label, value, bold, color }: { label: string; value: ReactNode; bold?: boolean; color?: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-ink-soft">{label}</span>
      <span className={`${bold ? 'font-semibold' : ''} ${color || 'text-green-900'}`}>{value}</span>
    </div>
  );
}

function PrintedSummaryBlock({
  summary,
  recon,
}: {
  summary: NonNullable<ExtractorResult['printed_summary']>;
  recon?: ExtractorResult['summary_reconciliation'];
}) {
  const cd = recon?.charges_diff;
  const rd = recon?.credits_diff;
  const EPS = 0.005; // diffs are rounded to cents; anything >= $0.01 is a real gap
  const matches =
    (cd === null || cd === undefined || Math.abs(cd) <= EPS) &&
    (rd === null || rd === undefined || Math.abs(rd) <= EPS);
  return (
    <div className="mt-2 border border-border rounded-control bg-cream-50 p-3 space-y-2">
      <div className="t-eyebrow">
        Statement's printed summary box
      </div>
      <Row label="Previous balance" value={fmtMoney(summary.previous_balance)} />
      <Row label="Payments & credits" value={fmtMoney(summary.payments_credits_total)} />
      <Row label="Purchases" value={fmtMoney(summary.purchases_total)} />
      {summary.fees_total !== null && (
        <Row label="Fees charged" value={fmtMoney(summary.fees_total)} />
      )}
      {summary.interest_total !== null && (
        <Row label="Interest charged" value={fmtMoney(summary.interest_total)} />
      )}
      <Row label="New balance" value={fmtMoney(summary.new_balance)} bold />
      {recon && (
        <>
          <div className="border-t border-border-subtle pt-2 mt-2"></div>
          <Row
            label="Charges: extracted vs printed"
            value={`${fmtMoney(recon.extracted_charges_total)} / ${fmtMoney(recon.printed_charges_total)}`}
            color={cd !== null && cd !== undefined && Math.abs(cd) > EPS ? 'text-warn' : 'text-green-700'}
          />
          <Row
            label="Credits: extracted vs printed"
            value={`${fmtMoney(recon.extracted_credits_total)} / ${fmtMoney(recon.printed_credits_total)}`}
            color={rd !== null && rd !== undefined && Math.abs(rd) > EPS ? 'text-warn' : 'text-green-700'}
          />
          {recon.pinpoint && (
            <div
              className={`text-xs mt-1 rounded-control p-2 ${
                matches
                  ? 'bg-good-soft border border-good-accent text-good'
                  : 'bg-warn-soft border border-warn-accent text-warn'
              }`}
            >
              {recon.pinpoint}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DiffList({ title, items }: { title: string; items: Array<{ date?: string; description?: string; amount?: number }> }) {
  return (
    <div>
      <div className="t-eyebrow mb-2">{title} ({items.length})</div>
      {items.length === 0 ? (
        <div className="text-xs text-ink-faint italic">None — same transactions extracted</div>
      ) : (
        <ul className="text-xs space-y-1 max-h-64 overflow-y-auto">
          {items.map((t, i) => (
            <li key={i} className="border-l-2 border-warn-accent pl-2">
              <span className="text-ink-soft">{t.date || '?'}</span>{' '}
              <span className={typeof t.amount === 'number' && t.amount < 0 ? 'text-danger' : 'text-green-700'}>
                {fmtMoney(t.amount ?? 0)}
              </span>{' '}
              <span className="text-ink">{t.description || ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RawTextPanel({ label, visible, onToggle, text, chars }: { label: string; visible: boolean; onToggle: () => void; text: string; chars: number }) {
  return (
    <Card padding="default">
      <Button
        variant="ghost"
        size="sm"
        onClick={onToggle}
        className="px-0 text-sm hover:bg-transparent hover:underline"
      >
        {visible ? '▾' : '▸'} {label} ({COUNT.format(chars)} chars)
      </Button>
      {visible && (
        <pre className="mt-2 text-xs bg-cream-50 p-3 rounded-control overflow-auto max-h-96 whitespace-pre-wrap font-mono">
          {text || '(not returned in this response — see backend logs)'}
        </pre>
      )}
    </Card>
  );
}
